import { getFingerprint } from '@/lib/fingerprint';
import { SITE_ORIGIN, nativeApp } from '@/lib/native';
import { platform } from '@/lib/platform';
// HTTP-клиент: access-токен в памяти, refresh — httpOnly cookie (+ CSRF double-submit),
// Idempotency-Key на всех изменяющих запросах, автообновление токена, редирект на /login при 401.
import { noteServerTime } from '@/lib/serverTime';

/**
 * Внутри платформ (Telegram, VK, Яндекс) игра часто открыта в iframe — сторонние cookie там
 * заблокированы. Тогда refresh-токен приходит в теле ответа (как у нативного приложения)
 * и хранится в localStorage. Яндекс Игры раздают игру со своего домена — API по абсолютному адресу.
 * Android-приложение: игра лежит в APK, API — на сайте игры, тоже по токенам.
 */
const TOKEN_MODE = !!platform || nativeApp;
export const API_BASE: string =
  import.meta.env.VITE_API_BASE ??
  (nativeApp
    ? SITE_ORIGIN
    : platform === 'yandex' && document.baseURI
      ? new URL(document.baseURI).origin
      : '');
const RT_KEY = 'sk_rt';

function storedRefresh(): string | null {
  try {
    return localStorage.getItem(RT_KEY);
  } catch {
    return null;
  }
}
function storeRefresh(t: string | null): void {
  try {
    if (t) localStorage.setItem(RT_KEY, t);
    else localStorage.removeItem(RT_KEY);
  } catch {
    // хранилище недоступно
  }
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
}

let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;
const listeners = new Set<(token: string | null) => void>();

export function setAccessToken(t: string | null): void {
  accessToken = t;
  // Выход/истёкшая сессия в режиме токенов — забываем и refresh-токен
  if (t === null && TOKEN_MODE) storeRefresh(null);
  for (const l of listeners) l(t);
}
export function getAccessToken(): string | null {
  return accessToken;
}
export function onTokenChange(fn: (token: string | null) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function csrf(): string {
  const m = document.cookie.match(/(?:^|;\s*)sk_csrf=([^;]+)/);
  return m ? decodeURIComponent(m[1] ?? '') : '';
}

/** Обновление access-токена по refresh-cookie (одновременные вызовы склеиваются). */
export async function refreshSession(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const rt = TOKEN_MODE ? storedRefresh() : null;
      if (TOKEN_MODE && !rt) return false;
      const res = await fetch(`${API_BASE}/api/v1/auth/refresh`, {
        method: 'POST',
        credentials: TOKEN_MODE ? 'omit' : 'include',
        headers: TOKEN_MODE
          ? { 'content-type': 'application/json', 'x-client': 'native' }
          : { 'content-type': 'application/json', 'x-csrf-token': csrf() },
        body: TOKEN_MODE ? JSON.stringify({ refreshToken: rt }) : '{}',
      });
      if (!res.ok) {
        setAccessToken(null);
        if (TOKEN_MODE) storeRefresh(null);
        return false;
      }
      const data = (await res.json()) as { accessToken: string; refreshToken?: string };
      if (data.refreshToken) storeRefresh(data.refreshToken);
      setAccessToken(data.accessToken);
      return true;
    } catch {
      return false;
    } finally {
      setTimeout(() => {
        refreshing = null;
      }, 0);
    }
  })();
  return refreshing;
}

export interface RequestOptions {
  body?: unknown;
  auth?: boolean;
  /** Повторное использование ключа идемпотентности (при ретраях). */
  idempotencyKey?: string;
  signal?: AbortSignal;
  /** Тело — файл (картинка), а не JSON. */
  raw?: Blob;
}

function selectedCastle(): string | null {
  try {
    return localStorage.getItem('sk_castle');
  } catch {
    return null;
  }
}

async function doFetch(
  method: string,
  path: string,
  opts: RequestOptions,
  key: string | null,
): Promise<Response> {
  const headers: Record<string, string> = {
    accept: 'application/json',
    'x-device-fingerprint': getFingerprint(),
  };
  if (opts.raw) headers['content-type'] = opts.raw.type || 'application/octet-stream';
  else if (opts.body !== undefined) headers['content-type'] = 'application/json';
  if (accessToken && opts.auth !== false) headers.authorization = `Bearer ${accessToken}`;
  if (key) headers['idempotency-key'] = key;
  // Выбранный замок: у каждого замка свои ресурсы — сервер списывает и начисляет их этому замку
  const castleId = selectedCastle();
  if (castleId) headers['x-castle-id'] = castleId;
  if (TOKEN_MODE) headers['x-client'] = 'native';
  const sentAt = Date.now();
  const res = await fetch(`${API_BASE}/api/v1${path}`, {
    method,
    headers,
    credentials: TOKEN_MODE ? 'omit' : 'include',
    body: opts.raw ?? (opts.body !== undefined ? JSON.stringify(opts.body) : undefined),
    signal: opts.signal,
  });
  noteServerTime(Number(res.headers.get('x-server-time')), sentAt, Date.now());
  return res;
}

export async function api<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  const key = method === 'GET' ? null : (opts.idempotencyKey ?? crypto.randomUUID());
  let res: Response;
  try {
    res = await doFetch(method, path, opts, key);
    // Сервер занят (лимит запросов) или перезапускается — короткая пауза и повтор
    // (POST повторяется с тем же ключом идемпотентности — дважды не выполнится)
    for (
      let attempt = 1;
      attempt <= 2 && (res.status === 429 || res.status === 502 || res.status === 503);
      attempt++
    ) {
      await new Promise((r) => setTimeout(r, 700 * attempt));
      res = await doFetch(method, path, opts, key);
    }
  } catch {
    throw new ApiError(0, 'network', 'offline');
  }
  if (res.status === 401 && opts.auth !== false) {
    if (await refreshSession()) res = await doFetch(method, path, opts, key);
    if (res.status === 401) {
      setAccessToken(null);
      if (!location.pathname.startsWith('/login'))
        location.assign(`/login?next=${encodeURIComponent(location.pathname)}`);
    }
  }
  const text = await res.text();
  // Ответ не JSON (например, HTML-страница ошибки прокси) — понятная ошибка вместо сбоя разбора
  let data: unknown = null;
  try {
    data = text ? (JSON.parse(text) as unknown) : null;
  } catch {
    throw new ApiError(
      res.status,
      res.status === 429 ? 'busy' : res.status >= 500 ? 'server_unavailable' : 'bad_response',
      res.status === 429
        ? 'Слишком много запросов — повторите через секунду'
        : 'Сервер временно недоступен — повторите через несколько секунд',
    );
  }
  // Режим токенов: новый refresh-токен после входа/регистрации — в хранилище
  if (TOKEN_MODE && res.ok && data && typeof data === 'object' && 'refreshToken' in data)
    storeRefresh((data as { refreshToken: string }).refreshToken);
  if (!res.ok) {
    const e = (data as { error?: { code: string; message: string; details?: unknown } } | null)?.error;
    throw new ApiError(res.status, e?.code ?? 'http_error', e?.message ?? res.statusText, e?.details);
  }
  return data as T;
}

// Одинаковые GET-запросы, ушедшие одновременно (пачка событий сокета, несколько экранов перечитывают
// одно и то же), объединяются в один: остальные получают копию ответа. Иначе всплеск событий рождал
// десятки одинаковых запросов в секунду, и сервер отвечал «слишком много запросов».
const inflight = new Map<string, Promise<unknown>>();

export const get = <T>(path: string, opts?: RequestOptions): Promise<T> => {
  if (opts?.signal || opts?.auth === false) return api<T>('GET', path, opts);
  const key = `${selectedCastle() ?? ''}|${path}`;
  const running = inflight.get(key);
  if (running) return running.then((data) => JSON.parse(JSON.stringify(data ?? null)) as T);
  const p = api<T>('GET', path, opts).finally(() => inflight.delete(key));
  inflight.set(key, p);
  return p;
};
export const post = <T>(path: string, body?: unknown, opts?: RequestOptions) =>
  api<T>('POST', path, { ...opts, body: body ?? {} });
export const put = <T>(path: string, body?: unknown) => api<T>('PUT', path, { body: body ?? {} });
export const del = <T>(path: string) => api<T>('DELETE', path);
